/**
 * Pure conversation id lifecycle state machine (PL-260504 §3).
 * No React — intended for useConversationLifecycle (α2) wiring.
 */

import type { ConversationMode } from '@shared/conversation-types';

/** @see PL-260504 §3.1 — extended with the route signals needed by §3.2. */
export type LifecycleState = {
  conversationId: string | null;
  mode: ConversationMode;
  isReady: boolean;
  lastActivityAt: number;
  isEnded: boolean;
  /**
   * Why the conversation ended, when `isEnded` is true.
   * - `guardrail` — the hardened guardrail tripped; UI shows the streamed message
   *   + a "Start again" affordance that routes home.
   * - `complete` / `scope_locked` — ANA-575: the agent intentionally ended the
   *   session (a voice goodbye / a scoping lock-and-close). Terminal, NOT
   *   resumable in place (a fresh conversation starts anew; scoping shows the
   *   Scoping Locked screen).
   * - `cap` — ANA-575: a time/turn cap ended the call. Terminal for THIS call but
   *   RESUMABLE (a cap is not a deliberate finish), same as an idle end.
   * - `null` — ended normally from inactivity (idle TTL) / not ended. Resumable.
   */
  endReason: 'guardrail' | 'complete' | 'scope_locked' | 'cap' | null;
  pendingReset: boolean;
  /** Last route project shortId; `null` = home (or unknown). Used for lazy reset detection. */
  activeShortId: string | null;
  /**
   * Spec 012 — the project this conversation thread is *committed* to (distinct
   * from `activeShortId`, the current route). `null` = unbound (lobby). The
   * thread follows the user across this project's pages and back to home; it
   * resets only when opening a *different* project. Talking in home mode clears
   * this (detaches), so a project discussed in the lobby continues when entered.
   */
  boundProjectId: string | null;
  /**
   * ANA-309 (FR-002d) — the id recovered from sessionStorage on mount (`null`
   * when none was recovered or after a re-mint). Lets a consumer distinguish a
   * stale, unconfirmed recovered id from a user-minted/reset id, so a
   * not-resumable recovered id can be cleared exactly once without looping on
   * the freshly-minted replacement.
   */
  recoveredId: string | null;
};

export type LifecycleEvent =
  | { kind: 'pathname_changed'; pathname: string; shortId: string | null }
  | { kind: 'user_message_sent'; at: number }
  /** ANA-309 / FR-002b — adopt the server-confirmed resumable id from find-on-mount. */
  | { kind: 'adopt_found_conversation'; conversationId: string; at: number }
  | { kind: 'cloud_idle_ended' }
  | { kind: 'cloud_guardrail_ended' }
  /**
   * ANA-575 — the agent INTENTIONALLY ended the session (a voice goodbye / a
   * scoping lock-and-close / a cap). Sets `isEnded` + the terminal `reason` so
   * the surface can show the right close (Scoping Locked / ended banner) and so
   * `resume_from_ended` won't re-open a deliberately-closed call. Distinct from
   * the idle-TTL end (`cloud_idle_ended`), which is resumable with reason `null`.
   */
  | { kind: 'cloud_session_ended'; reason: 'complete' | 'scope_locked' | 'cap' }
  /**
   * Re-open an idle/normally-ended conversation *in place* — the user chose
   * "pick up where you left off" on the returning entry card. Clears the ended
   * flags but keeps the id + transcript (resume, not cold reset), so the next
   * reconnect turn continues the SAME conversation (server welcome-back recap).
   * No-op on a guardrail termination (a hard stop — callers route home instead).
   */
  | { kind: 'resume_from_ended'; at: number }
  | { kind: 'cloud_mode_change'; mode: ConversationMode }
  | { kind: 'explicit_reset'; at: number }
  /**
   * Hard tenancy reset (e.g. organisation switch): drop the conversation
   * entirely — id, binding, transcript — back to a clean, conversation-less
   * slate so nothing from the previous org survives into the new tenant. Unlike
   * `explicit_reset` this does NOT mint a fresh id (lazy: a new id is minted
   * only when the user next speaks), so the new org starts with no active thread.
   */
  | { kind: 'cleared'; at: number }
  | {
      kind: 'recover_from_storage';
      conversationId: string;
      mode: ConversationMode;
      /** Route shortId persisted with the session — hydrates `activeShortId` so post-recovery `pathname_changed` matches don’t false-trigger lazy reset. */
      shortId: string | null;
      /** Spec 012 — the bound project persisted with the session (S8). */
      boundProjectId: string | null;
      at: number;
    };

/**
 * Maps a route pathname to the default conversation mode for that page.
 * `/projects/:id/scoping` is route-derived to `scoping` mode (017). Interview
 * mode is still entered via `cloud_mode_change`, not from URLs.
 */
export function deriveModeFromPathname(pathname: string): ConversationMode {
  if (pathname === '' || pathname === '/' || pathname === '/chat') {
    // `/chat` is the dedicated full-page home conversation surface — same mode
    // as the lobby so navigating `/` → `/chat` keeps the surface key stable (a
    // mode flip would reset the transport and wipe the transcript) and the
    // home-only recent-conversation context seeding still applies.
    return 'home';
  }
  if (/^\/projects\/[^/]+\/scoping(?:\/|$)/.test(pathname)) {
    return 'scoping';
  }
  if (/^\/projects\/[^/]+/.test(pathname)) {
    return 'update';
  }
  return 'update';
}

export function createInitialState(now: number): LifecycleState {
  return {
    conversationId: null,
    mode: 'home',
    isReady: false,
    lastActivityAt: now,
    isEnded: false,
    endReason: null,
    pendingReset: false,
    activeShortId: null,
    boundProjectId: null,
    recoveredId: null,
  };
}

export function reduce(state: LifecycleState, event: LifecycleEvent): LifecycleState {
  switch (event.kind) {
    case 'pathname_changed': {
      // Spec 012 — bound-project navigation (R1). The event carries the route
      // shortId (`null` = home). The thread is bound to `boundProjectId` and
      // only resets when opening a project different from the bound one.
      const mode = deriveModeFromPathname(event.pathname);
      const targetProject = event.shortId; // null = home/lobby route

      if (targetProject == null) {
        // → home: retain thread, home mode, KEEP the binding (S3). Binding
        // governs navigation only; the home assistant uses generic lobby
        // context regardless (FR-G-010).
        return { ...state, mode, activeShortId: null };
      }

      // → project P. Retain + bind when unbound or re-entering the bound
      // project (S1/S2/S4/S6); reset when it's a *different* bound project (S5/S7).
      const isDifferentBoundProject =
        state.boundProjectId !== null && state.boundProjectId !== targetProject;

      if (isDifferentBoundProject) {
        // RESET — immediate id mint (D1).
        return {
          ...state,
          mode,
          activeShortId: targetProject,
          boundProjectId: targetProject,
          conversationId: crypto.randomUUID(),
          pendingReset: false,
          isReady: true,
          isEnded: false,
        };
      }

      // RETAIN + bind.
      return {
        ...state,
        mode,
        activeShortId: targetProject,
        boundProjectId: targetProject,
      };
    }
    case 'user_message_sent': {
      const needsNewId = state.pendingReset || state.conversationId === null;
      const conversationId = needsNewId ? crypto.randomUUID() : state.conversationId;

      return {
        ...state,
        conversationId,
        pendingReset: false,
        lastActivityAt: event.at,
        isEnded: false,
        endReason: null,
        isReady: conversationId !== null,
        // R2 (Spec 012) — talking in home detaches the thread, so a project
        // discussed in the lobby is *continued* (not reset) when entered.
        boundProjectId: state.mode === 'home' ? null : state.boundProjectId,
      };
    }
    case 'adopt_found_conversation':
      // FR-002b — a fresh client (cleared storage / new device) adopts the
      // server-confirmed resumable id from find-on-mount. Resume, not cold: the
      // next user message continues this conversation. Never clobber an
      // already-active id (a user-minted or recovered conversation wins).
      if (state.conversationId !== null) return state;
      return {
        ...state,
        conversationId: event.conversationId,
        isReady: true,
        lastActivityAt: event.at,
      };
    case 'cloud_idle_ended':
      return { ...state, isEnded: true };
    case 'cloud_guardrail_ended':
      return { ...state, isEnded: true, endReason: 'guardrail' };
    case 'cloud_session_ended':
      // ANA-575 — an intentional agent-driven end. Idempotent-friendly: last
      // reason wins. `complete`/`scope_locked` are terminal (not resumable);
      // `cap` is terminal-for-this-call but resumable (see `resume_from_ended`).
      return { ...state, isEnded: true, endReason: event.reason };
    case 'resume_from_ended':
      // Re-open WITHOUT minting a new id or clearing the transcript: the next
      // reconnect turn continues the SAME conversation (recap, not cold
      // greeting). Referential no-op unless the end is RESUMABLE — the idle-TTL
      // end (reason `null`) or a `cap` end. A guardrail stop and an intentional
      // `complete`/`scope_locked` end are terminal and never re-opened in place;
      // a stray dispatch can't disturb a live conversation either.
      if (!state.isEnded) return state;
      if (state.endReason !== null && state.endReason !== 'cap') return state;
      return {
        ...state,
        isEnded: false,
        endReason: null,
        lastActivityAt: event.at,
      };
    case 'cloud_mode_change':
      return {
        ...state,
        mode: event.mode,
      };
    case 'explicit_reset': {
      return {
        ...state,
        conversationId: crypto.randomUUID(),
        isEnded: false,
        endReason: null,
        pendingReset: false,
        lastActivityAt: event.at,
        isReady: true,
        // A hard reset is unbound (Spec 012).
        boundProjectId: null,
      };
    }
    case 'cleared':
      // Tenancy reset — back to a clean slate (home, no id, unbound). Lazy: no
      // id is minted here, so no conversation exists until the user speaks in
      // the new org.
      return createInitialState(event.at);
    case 'recover_from_storage':
      return {
        ...state,
        conversationId: event.conversationId,
        recoveredId: event.conversationId,
        mode: event.mode,
        activeShortId: event.shortId,
        boundProjectId: event.boundProjectId,
        isReady: true,
        lastActivityAt: event.at,
        isEnded: false,
        endReason: null,
        pendingReset: false,
      };
    default: {
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
}
